import type { Compartment, Planogram } from '@/lib/planogram-api';

/**
 * MAPA SZAFY DO DRUKU — układ, kolory i legenda. Bez i18n i bez pdfmake, żeby dało się to testować.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZAMÓWIONE 02.09.2026 ZE WZORCA ZE STAREGO VENDI (rozebrany PDF „Planogram Rembud Gorzow
 *  - 1 VMID 1"): oś X to kolumny bębna, oś Y to wiersze, każda przegródka na kolorze swojego
 *  towaru, numer w przegródce odpowiada numerowi w legendzie. Numer jest tam PO TO, żeby wydruk
 *  czarno-biały nadal dawał się czytać — i dlatego jest tu, a nie tylko kolor.
 *
 *  ⚠️ TRZY STANY PRZEGRÓDKI, NIE DWA — dokładnie jak na ekranie (`Cell` w `Planogram.tsx`):
 *  zajęta (kolor towaru), PUSTA (biała, wiersz w bazie jest) i NIE ISTNIEJĄCA (szara, wiersza
 *  w bazie nie ma). Zlanie dwóch ostatnich kłamałoby o tym, co da się zapełnić.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/**
 * Poszerzenie bywa ×2 i ×3; szerszego NIE rysujemy w skali, bo to raczej dziura w danych.
 *
 * ⚠️ WSPÓLNY Z EKRANEM (`Planogram.tsx` bierze go stąd). Rozjazd tych dwóch liczb znaczyłby, że
 * wydruk pokazuje inną szafę niż siatka, na którą operator patrzy przy tym samym automacie.
 */
export const MAX_SPAN_RYSOWANY = 3;

/**
 * ILE MIEJSC BĘBNA TRZYMA JEDNA BLACHA — granica segmentu, ponad którą nie da się scalić kolumn.
 *
 * ⚠️⚠️ TO JEST ZAŁOŻENIE, NIE POMIAR, i wydruk musi tak o tym mówić. SPS nie zapisuje segmentów
 * ŻADNĄ kolumną (`Drawers` ma trzynaście kolumn i nie ma wśród nich rozmiaru — patrz
 * `api/src/planogram/geometry.ts`), a `DrawerChannels`/`DrawerLids` są u klientów puste.
 * Trójka jest odczytana ze wzorcowego PDF-a starego Vendi: grube linie stoją tam co 337,5 pt przy
 * miejscu szerokim 112,5 pt, czyli co trzy miejsca. Dzieli się równo i dla bębna 36-, i 54-miejscowego.
 */
export const MIEJSC_W_SEGMENCIE = 3;

/** Kolor przegródki: po towarze (jak wzorzec) albo po grupie artykułów (mniej kolorów). */
export type TrybKoloru = 'towar' | 'grupa';

export type PozycjaLegendy = {
  /** Numer w legendzie — ten sam stoi w przegródce. Numeracja jest ALFABETYCZNA po nazwie. */
  nr: number;
  itemId: string;
  nazwa: string;
  numerTowaru: string;
  grupa: string | null;
  /** Kolor tła przegródki — zależy od trybu, numer nie. */
  kolor: string;
  /** Ile przegródek i ile sztuk maksymalnie ma ten towar w danym bębnie (`picNumber`). */
  wBebnach: Map<number, { przegrodek: number; maks: number }>;
};

/** Towar bez grupy dostaje własny, stały kolor — inaczej wpadłby do jednego worka z „nie wiem". */
export const KOLOR_BEZ_GRUPY = '#d8dae0';
/** Przegródka pusta (wiersz jest, towaru nie ma). */
export const KOLOR_PUSTEJ = '#ffffff';
/** Wiersza nie ma w bazie — inny stan niż „pusta", więc inny kolor. */
export const KOLOR_BRAKU = '#e2e2e2';

/**
 * KOLOR Z POZYCJI NA LIŚCIE — kąt złoty, czyli maksymalne rozsunięcie odcieni przy KAŻDEJ liczbie
 * towarów. Losowanie po identyfikatorze dawałoby dwa sąsiednie towary w tym samym odcieniu.
 *
 * ⚠️ JASNOŚĆ STAŁA I WYSOKA, bo w przegródce stoi czarny tekst — nazwa towaru i stan muszą być
 * czytelne na każdym z tych kolorów. Kolor nigdy nie niesie wartości sam: obok stoi numer.
 */
export function kolorPozycji(i: number): string {
  return hslNaHex((i * 137.508) % 360, 60, 84);
}

function hslNaHex(h: number, s: number, l: number): string {
  const sn = s / 100;
  const ln = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n: number) => ln - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const bajt = (v: number) =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${bajt(f(0))}${bajt(f(8))}${bajt(f(4))}`;
}

/**
 * LEGENDA CAŁEJ MASZYNY — numery i kolory są WSPÓLNE dla wszystkich bębnów.
 *
 * ⚠️ NUMERACJA ALFABETYCZNA PO NAZWIE (tak samo jak we wzorcu): po legendzie szuka się nazwy,
 * więc kolejność musi być ta, w której człowiek szuka. Numer towaru nie nadaje się na klucz
 * sortowania — u części klientów jest nim ta sama nazwa, u części kod maszynowy.
 *
 * ⚠️ NUMER NIE ZALEŻY OD TRYBU KOLORU. Ten sam wydruk w kolorze po grupie ma pokazywać te same
 * numery co w kolorze po towarze, inaczej dwa arkusze tej samej szafy nie dałyby się porównać.
 */
export function legendaMapy(drawers: Planogram['drawers'], tryb: TrybKoloru): PozycjaLegendy[] {
  type Zebrane = Omit<PozycjaLegendy, 'nr' | 'kolor'>;
  const towary = new Map<string, Zebrane>();

  for (const kolumna of drawers) {
    for (const c of kolumna.compartments) {
      if (c.item === null) continue;
      let wpis = towary.get(c.item.id);
      if (wpis === undefined) {
        wpis = {
          itemId: c.item.id,
          nazwa: c.item.name,
          numerTowaru: c.item.itemNumber,
          grupa: c.item.groupName,
          wBebnach: new Map(),
        };
        towary.set(c.item.id, wpis);
      }
      const wBebnie = wpis.wBebnach.get(kolumna.picNumber) ?? { przegrodek: 0, maks: 0 };
      wBebnie.przegrodek += 1;
      wBebnie.maks += c.qtyMax ?? 0;
      wpis.wBebnach.set(kolumna.picNumber, wBebnie);
    }
  }

  const uporzadkowane = [...towary.values()].sort(
    (a, b) => a.nazwa.localeCompare(b.nazwa, 'pl') || a.numerTowaru.localeCompare(b.numerTowaru, 'pl'),
  );

  /** Kolory grup liczone z osobnej, też alfabetycznej listy — grupa ma jeden kolor na całą szafę. */
  const grupy = [...new Set(uporzadkowane.map((w) => w.grupa).filter((g): g is string => g !== null))].sort((a, b) =>
    a.localeCompare(b, 'pl'),
  );

  return uporzadkowane.map((w, i) => ({
    ...w,
    nr: i + 1,
    kolor:
      tryb === 'towar'
        ? kolorPozycji(i)
        : w.grupa === null
          ? KOLOR_BEZ_GRUPY
          : kolorPozycji(grupy.indexOf(w.grupa)),
  }));
}

/** Ile linii zajmuje nazwa towaru w przegródce — ZAWSZE tyle, patrz `nazwaWKomorce`. */
export const LINII_NAZWY = 2;

/**
 * NAZWA TOWARU ROZŁAMANA NA DOKŁADNIE DWIE LINIE.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ „ZAWSZE DWIE" JEST TU CAŁĄ TREŚCIĄ, nie przycięciem. Zgłoszone 02.09.2026: „w pdf numer
 *  jest po prawej stronie, ale nie jest w dolnym rogu, tylko wisi gdzieś w górnej połowie — raz
 *  wyżej raz niżej". Powód: stan i numer stały POD nazwą, a nazwa zajmowała raz jedną, raz dwie,
 *  raz trzy linie — więc dolny wiersz komórki startował za każdym razem na innej wysokości.
 *  pdfmake nie umie wyrównać treści do dołu komórki, więc jedyny sposób na stałą wysokość to
 *  stała liczba linii nad nią. Puste linie wypełniamy spacją, bo samo `\n` na końcu bywa gubione.
 *
 *  ⚠️ ŁAMIEMY SAMI, NIE ODDAJEMY TEGO pdfmake. Ta sama nazwa musi dać ten sam podział przy każdym
 *  generowaniu; zdanie na łaskę zawijania w bibliotece znaczyłoby, że o wysokości dolnego wiersza
 *  decyduje szerokość glifu — a wtedy „raz wyżej, raz niżej" wraca.
 *
 *  ⚠️ PEŁNA NAZWA JEST W LEGENDZIE, więc obcięcie w przegródce niczego nie ukrywa.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function nazwaWKomorce(nazwa: string, naZnakow: number): string[] {
  const limit = Math.max(3, naZnakow);
  const linie: string[] = [];
  let reszta = nazwa.trim().replace(/\s+/g, ' ');

  while (reszta !== '' && linie.length < LINII_NAZWY) {
    if (reszta.length <= limit) {
      linie.push(reszta);
      reszta = '';
      break;
    }
    /** Łamiemy na spacji, ale tylko gdy nie zostawia połowy linii pustej — inaczej twardo. */
    const spacja = reszta.lastIndexOf(' ', limit);
    const przy = spacja > Math.floor(limit / 2) ? spacja : limit;
    linie.push(reszta.slice(0, przy).trimEnd());
    reszta = reszta.slice(przy).trimStart();
  }

  /** Co się nie zmieściło, znaczymy wielokropkiem na ostatniej linii. */
  if (reszta !== '' && linie.length > 0) {
    const i = linie.length - 1;
    linie[i] = `${linie[i]!.slice(0, Math.max(0, limit - 1)).trimEnd()}…`;
  }
  while (linie.length < LINII_NAZWY) linie.push('');
  return linie;
}

export type KolumnaMapy = {
  /** SPS `DRAWERNUMBER` — numer kolumny bębna, dokładnie jak w bazie klienta. */
  drawerNo: number;
  /** Ile miejsc bębna zajmuje (1, 2 albo 3 przy poszerzeniu blachą). */
  span: number;
  /** Szerokość RYSOWANA — przycięta do `MAX_SPAN_RYSOWANY`, bo większy przeskok to raczej dziura. */
  spanRysowany: number;
  /** Szerokość ostatniej kolumny jest założona — nie ma za nią czego zmierzyć. */
  spanZalozony: boolean;
  /** Przegródki po POZYCJI W KOLUMNIE (`rowNo`), `null` = w bazie nie ma tego wiersza. */
  komorki: (Compartment | null)[];
};

export type UkladBebna = {
  kolumny: KolumnaMapy[];
  /** Suma szerokości rysowanych — tyle miejsc bębna zajmuje rysunek. */
  miejsca: number;
  /**
   * Indeksy PIONOWYCH krawędzi tabeli, które mają być grube (granice segmentów).
   *
   * Numeracja jak w `vLineWidth` pdfmake: 0 to lewa krawędź kolumny etykiet, 1 to początek
   * siatki, `1 + k` to krawędź za k-tą kolumną.
   */
  grubeKrawedzie: Set<number>;
};

/**
 * UKŁAD JEDNEGO BĘBNA — kolumny w kolejności rysowania i przegródki poukładane po `rowNo`.
 *
 * ⚠️ WYSOKOŚĆ Z TEGO BĘBNA, nie z maszyny. Zmierzone na PESA (28.08.2026): szafka o pięciu
 * drzwiczkach rysowała się na trzynaście wierszy, bo brała maksimum po wszystkich modułach
 * automatu — i operator dostawał miejsca, których za tymi drzwiczkami nie ma.
 *
 * ⚠️ PRZEGRÓDKI PO `rowNo`, NIGDY po pozycji w tablicy ani po numerze SPS. DynaBox rozdziela te
 * dwie liczby: pierwsza kolumna trzyma przegródki 1, 55, 109 … 649.
 */
export function ukladBebna(drawers: Planogram['drawers'], wiersze: number): UkladBebna {
  const kolumny: KolumnaMapy[] = drawers.map((d) => {
    const poWierszu = new Map(d.compartments.map((c) => [c.rowNo, c]));
    return {
      drawerNo: d.drawerNo,
      span: d.span,
      spanRysowany: Math.min(d.span, MAX_SPAN_RYSOWANY),
      spanZalozony: d.spanAssumed,
      komorki: Array.from({ length: wiersze }, (_, i) => poWierszu.get(i + 1) ?? null),
    };
  });

  const grubeKrawedzie = new Set<number>();
  let miejsca = 0;
  kolumny.forEach((k, i) => {
    if (miejsca % MIEJSC_W_SEGMENCIE === 0) grubeKrawedzie.add(1 + i);
    miejsca += k.spanRysowany;
  });
  if (miejsca % MIEJSC_W_SEGMENCIE === 0) grubeKrawedzie.add(1 + kolumny.length);

  return { kolumny, miejsca, grubeKrawedzie };
}

// ─────────────────────────────── wymiary strony ──────────────────────────────

/** Miejsce bębna i wiersz w punktach — z rozebranego wzorca (112,5 × 72,7 pt). */
export const SLOT_PT = 108;
export const WIERSZ_PT = 64;
export const ETYKIETA_PT = 30;
export const MARGINES_PT = 24;
/**
 * Wysokość nagłówka strony (tytuł, moduł, podpis wydruku, linia konwencji) i wiersza nagłówków
 * kolumn. `ODSTEP_PT` niesie tytuł legendy razem z odstępem nad nią.
 *
 * ⚠️ Z ZAPASEM, i to jest celowe: wysokość strony liczymy Z GÓRY, a pdfmake, gdy treść nie
 * mieści się o dwa punkty, dokłada DRUGĄ stronę tego samego, gigantycznego rozmiaru. Pasek
 * pustego papieru na dole jest tańszy niż druga strona z jednym wierszem legendy.
 */
export const NAGLOWEK_PT = 64;
export const NAGLOWEK_KOLUMN_PT = 18;
export const ODSTEP_PT = 28;
/** Jedna pozycja legendy: szerokość kolumny i wysokość wiersza. */
export const LEGENDA_SZER_PT = 250;
export const LEGENDA_WIERSZ_PT = 26;

/**
 * ODSTĘPY W KOMÓRCE — te same, które `layout` podaje pdfmake.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZMIERZONE, NIE ZAŁOŻONE (02.09.2026, na prawdziwym złożonym pliku): `widths` i `heights`
 *  w pdfmake podają wymiar TREŚCI, a odstępy komórki dochodzą do niego z zewnątrz. Skutki były
 *  dwa i oba ciche:
 *
 *    • wiersz zamówiony na 64 pt zajmował 68 pt, jedenaście wierszy rozjeżdżało się o 44 pt
 *      i legenda spadała na DRUGĄ stronę tego samego, dwumetrowego arkusza;
 *    • kolumna zamówiona na 108 pt zajmowała 114 pt, więc siatka 54-miejscowa wychodziła
 *      ~320 pt ZA PRAWĄ KRAWĘDŹ strony — i po prostu się ucinała, bez żadnego błędu.
 *
 *  Dlatego wymiary podajemy POMNIEJSZONE o odstępy: miejsce bębna ma wtedy dokładnie `SLOT_PT`,
 *  a granice segmentów wypadają co równe trzy miejsca, jak we wzorcu.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export const ODSTEP_KOMORKI_PT = 2;
export const ODSTEP_POZIOMY_PT = 3;

/**
 * REZERWA NA GRUBOŚĆ KRESEK — na każdą kolumnę i na obie krawędzie.
 *
 * ⚠️ Kreski też zajmują miejsce (0,75 pt zwykła, 2,5 pt granica segmentu) i przy czterdziestu
 * czterech kolumnach składa się z tego kilkadziesiąt punktów. Dokładność co do kreski nie jest
 * osiągalna z góry, a niedoszacowanie ucina prawy brzeg siatki.
 */
export const KRESKA_ZAPAS_PT = 2;

/**
 * ZAPAS NA DOLE STRONY.
 *
 * ⚠️ Wysokość liczymy z góry, a złożenie zależy jeszcze od wysokości linii czcionki, której nie
 * przewidzimy co do punktu. Pasek pustego papieru jest niewidoczny, druga strona z jednym
 * wierszem legendy — nie.
 */
export const ZAPAS_PT = 24;

/**
 * ⚠️ FORMAT PDF NIE ZNIESIE DOWOLNIE WIELKIEJ STRONY — twardy limit to 14 400 pt (200 cali) na
 * wymiar. Bęben 54-miejscowy w skali wzorcowej daje ~5 900 pt, więc mieścimy się z zapasem, ale
 * jedna maszyna z rozjechaną numeracją (przeskoki brane za poszerzenia) potrafi tę liczbę
 * podwoić. Wtedy ZWĘŻAMY miejsce, zamiast wypuszczać plik, którego nie otworzy żadna przeglądarka.
 */
export const MAX_WYMIAR_PT = 14000;
/** Poniżej tej szerokości nazwa towaru w przegródce jest nieczytelna — zostaje numer i stan. */
export const MIN_SLOT_Z_NAZWA_PT = 56;

/**
 * WYSOKOŚCI LINII TEKSTU W PRZEGRÓDCE — zmierzone na złożonym pliku, nie policzone z czcionki.
 *
 * ⚠️ SŁUŻĄ DO JEDNEGO: odepchnięcia wiersza ze stanem i numerem do DOLNEJ krawędzi przegródki
 * (`odstepDoDolu`). pdfmake nie zna wyrównania do dołu, więc odstęp trzeba policzyć — a do tego
 * potrzebne są wysokości tego, co stoi wyżej.
 */
export const LINIA_NAZWY_PT = 8.6;
/**
 * Cała wysokość NAZWY w komórce: dwie linie plus odstęp wiodący nad pierwszą.
 *
 * ⚠️ ZMIERZONE NA ZŁOŻONYM PLIKU: pierwsza linia startuje 3,5 pt pod krawędzią treści.
 */
export const WYSOKOSC_NAZWY_PT = LINII_NAZWY * LINIA_NAZWY_PT + 3.5;
/**
 * Cała wysokość DOLNEGO wiersza (stan + numer na białym tle).
 *
 * ⚠️⚠️ ZMIERZONE NA ZŁOŻONYM PLIKU, w trzech podejściach — i warto wiedzieć, czego NIE mierzy.
 * Sam rząd kolumn (stan po lewej, numer po prawej) zajmuje ~9 pt. Wcześniejsze 17 pt brało się
 * z pomyłki w diagnozie: wiersze rzeczywiście rosły, ale rosły od TRZECIEJ LINII NAZWY, którą
 * pdfmake dokładał sam przy zbyt optymistycznym szacunku szerokości znaku — nie od tego bloku.
 * Po przejęciu łamania nazwy (`nazwaWKomorce`) trzeciej linii nie ma i te 9 pt się zgadza:
 * zmierzony odstęp numeru od dolnej krawędzi to ~6 pt, a wiersz zostaje na zamówionej wysokości.
 */
export const WYSOKOSC_DOLU_PT = 14;

/**
 * ILE ZOSTAJE NAD DOLNYM WIERSZEM PRZEGRÓDKI — czyli o tyle go odpychamy.
 *
 * ⚠️ NIGDY UJEMNIE: przy ciasnym wierszu dolna linia po prostu stoi zaraz pod nazwą, zamiast
 * wypychać komórkę i rozpychać całą siatkę.
 */
export function odstepDoDolu(wierszPt: number): number {
  const tresc = wierszPt - 2 * ODSTEP_KOMORKI_PT;
  return Math.max(0, Math.floor(tresc - WYSOKOSC_NAZWY_PT - WYSOKOSC_DOLU_PT));
}

export type ZamowienieWymiarow = {
  /** Ile MIEJSC bębna zajmuje rysunek (kolumna poszerzona zajmuje 2 albo 3). */
  miejsca: number;
  /** Ile jest KOLUMN — po jednej kresce na kolumnę, patrz `KRESKA_ZAPAS_PT`. */
  kolumn: number;
  wiersze: number;
  pozycjiLegendy: number;
};

export type WymiaryMapy = {
  slotPt: number;
  wierszPt: number;
  szerokosc: number;
  wysokosc: number;
  /** `false` = miejsce jest tak wąskie, że w przegródce zostaje sam numer z legendy i stan. */
  zNazwami: boolean;
  legendaKolumn: number;
  legendaWierszy: number;
};

/**
 * WYMIARY STRONY — jedna strona na bęben, cała szafa na jednym arkuszu (wybór z 02.09.2026).
 *
 * ⚠️ WYSOKOŚĆ LICZY SIĘ Z LEGENDĄ. Bez tego pdfmake dorzucał drugą stronę tego samego,
 * gigantycznego rozmiaru — z jednym paskiem legendy i metrem pustego papieru.
 */
export function wymiaryMapy({ miejsca, kolumn, wiersze, pozycjiLegendy }: ZamowienieWymiarow): WymiaryMapy {
  /** Wszystko poza samymi miejscami bębna: marginesy, kolumna etykiet i rezerwa na kreski. */
  const pozaSiatka = 2 * MARGINES_PT + ETYKIETA_PT + (kolumn + 2) * KRESKA_ZAPAS_PT;
  const slotPt = Math.max(12, Math.min(SLOT_PT, Math.floor((MAX_WYMIAR_PT - pozaSiatka) / Math.max(1, miejsca))));
  const szerokosc = pozaSiatka + slotPt * miejsca;

  const legendaKolumn = Math.max(1, Math.floor((szerokosc - 2 * MARGINES_PT) / LEGENDA_SZER_PT));
  const legendaWierszy = Math.ceil(pozycjiLegendy / legendaKolumn);

  /** Wszystko poza samymi wierszami siatki — to zostaje wysokości wiersza do podziału. */
  const pozaWierszami =
    2 * MARGINES_PT +
    NAGLOWEK_PT +
    NAGLOWEK_KOLUMN_PT +
    ODSTEP_PT +
    legendaWierszy * LEGENDA_WIERSZ_PT +
    ZAPAS_PT +
    (wiersze + 2) * KRESKA_ZAPAS_PT;

  const wierszPt = Math.min(WIERSZ_PT, Math.max(14, Math.floor((MAX_WYMIAR_PT - pozaWierszami) / Math.max(1, wiersze))));
  const wysokosc = pozaWierszami + wiersze * wierszPt;

  return {
    slotPt,
    wierszPt,
    szerokosc,
    wysokosc,
    zNazwami: slotPt >= MIN_SLOT_Z_NAZWA_PT,
    legendaKolumn,
    legendaWierszy,
  };
}
